<template>
  <ItinerarySummaryCard
    type="flight"
    :price="price"
    :removable="true"
    @delete="onDelete()"
  >
    <div class="flex flex-col gap-16">
      <template
        v-for="(
          { segments, origin, destination, duration, baggageData }, index
        ) in slices"
        :key="index"
      >
        <FlightTimeline
          :arrivalDate="
            useFormatDate(
              segments[segments.length - 1].arrivingAt,
              'EEE dd MMM'
            ).formattedDate.value
          "
          :arrivalTime="convertToHHMM(segments[segments.length - 1].arrivingAt)"
          :departureDate="
            useFormatDate(segments[0].departingAt, 'EEE dd MMM').formattedDate
              .value
          "
          :selfTransfer="false"
          :supplierName="segments[0].marketingCarrier.airlineName"
          :departureTime="convertToHHMM(segments[0].departingAt)"
          :supplierLogo="segments[0].marketingCarrier.logoSymbolUrl"
          :operatedBy="getOperation(segments[0])"
          :path="[
            {
              label: origin.iataCode,
              title: convertTime(duration),
            },
            {
              label: destination.iataCode,
            },
          ]"
        >
          <template #caption>
            <div class="flex items-center">
              <span
                v-if="segments.length > 1"
                class="inline-flex gap-x-2 text-metadata-limited"
              >
                <span>{{ segments.length - 1 }}</span>
                <span>
                  {{
                    segments.length - 1 > 1
                      ? $t('search.stops')
                      : $t('search.stop')
                  }}
                </span>
              </span>

              <span v-else class="text-metadata-limited">
                {{ $t('search.direct') }}
              </span>

              <template
                v-for="(seg, idx) in item.slices[index].segments"
                :key="idx"
              >
                <JTooltip
                  v-if="idx !== segments.length - 1"
                  placement="bottom"
                  arrow
                  hover
                  offsetSkid="10"
                >
                  <template #activator>
                    <span class="ms-2 border-b text-metadata-limited">
                      {{ seg.destination.iataCode }}
                    </span>
                  </template>

                  <template #content>
                    <p class="min-w-[20rem] p-12 text-center">
                      {{ seg.destination.placeName }}
                    </p>
                  </template>
                </JTooltip>
              </template>
            </div>
          </template>
        </FlightTimeline>

        <div
          class="flex flex-col justify-between gap-y-12 md:flex-row md:items-center [&>div]:flex [&>div]:items-center [&>div]:gap-x-10 [&>div]:text-dark"
        >
          <div class="text-metadata-limited md:text-base">
            <!-- PERSONAL ITEM -->
            <span
              v-for="({ baggageType, quantity }, i) in baggageData"
              :key="i"
              class="flex items-center gap-x-4 text-gray-500"
            >
              <JIcon :name="getBaggageIcon(baggageType)" size="16" />
              <span class="font-medium">
                {{ quantity }}

                {{ $t(`search.baggage.${baggageType}`) }}
              </span>
            </span>
          </div>

          <div
            v-if="index === item.slices.length - 1"
            class="flex flex-row-reverse items-center justify-between md:flex-row"
          >
            <div class="flex items-center gap-x-12">
              <JIcon name="person" size="16" />
              <span>
                {{ item.passengers.length }}
                {{
                  item.passengers.length > 1
                    ? $t('search.passengers')
                    : $t('search.passenger')
                }}
              </span>
              <span
                class="hidden text-subject font-medium text-gray-300 md:inline-block"
                >|</span
              >
            </div>
            <div
              class="inline-flex cursor-pointer text-subject font-medium text-primary underline"
              @click="emit('showDetails', item)"
            >
              <span> {{ $t('search.flightDetails') }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </ItinerarySummaryCard>
</template>

<script setup lang="ts">
import { JIcon, JTooltip } from '@journey/ui-kit'
import { computed } from 'vue'
import ItinerarySummaryCard from '../ItinerarySummaryCard/ItinerarySummaryCard.vue'
import type { Emits, Props } from './ItinerarySummaryFlightCard.types'
import useItinerarySummary from '@/modules/Search/store/itinerarySummary'
import {
  useCurrency,
  useDateToHMMConverter,
  useFormatDate,
  useTimeConverter,
} from '@/modules/Core/composables'
import FlightTimeline from '@/modules/Trips/components/FlightTimeline/FlightTimeline.vue'
import { useItinerary } from '@/modules/Search/composables'
import type { FlightSegment } from '@/modules/Search/types/model/search'
import {
  getBaggageIcon,
  getSegmentsBaggageAllowances,
} from '@/modules/Core/utils'

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { convertTime } = useTimeConverter()
const { convertToHHMM } = useDateToHMMConverter()
const { formatCurrency } = useCurrency()
const itinerarySummaryStore = useItinerarySummary()

const slices = computed(() =>
  props.item.slices.map((slice) => ({
    ...slice,
    baggageData: getSegmentsBaggageAllowances({
      segments: slice.segments,
    }),
  }))
)

const price = computed(
  () =>
    formatCurrency(
      Number(props.item.totalAmount.toFixed(2)),
      props.item.convertedCurrency
    ).value
)

const { submitItinerary } = useItinerary()

async function onDelete() {
  itinerarySummaryStore.remove(props.item.uniqueId)

  const { data } = await submitItinerary()

  itinerarySummaryStore.setItineraries(data.data.itinerary)
}

function getOperation(segment: FlightSegment) {
  return segment.operatingCarrier.iataCode !== segment.marketingCarrier.iataCode
    ? segment.operatingCarrier.airlineName
    : ''
}
</script>
